import { useEffect, type ReactNode } from "react";
import { useTenantOrganizationId } from "../contexts/TenantIdentityContext";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useAppearancePolicy } from "../platform/PlatformProvider";
import { decideTenantDesignV2, leaveTenantDesignV2, useDesignV2Decided } from "./designV2Store";

interface DesignV2GateProps {
    /** Drawn until the design is known; it must show neither design (`BootstrapShell`). */
    fallback: ReactNode;
    children: ReactNode;
}

/**
 * Holds the admitted project's pages until its design is known, and decides it for
 * `RedesignProvider`, which sits above admission and the tenant query client so a design switch
 * remounts neither. A page mounted in the design the project does not use would be torn down and
 * mounted again in the other: classic would flash, and its `page_load` would report twice.
 *
 * The design is known once this browser has chosen one (its choice wins), or once the first read
 * of the project's flag ends: the server's answer, or, once an attempt fails, the project's last
 * stored flags (`useFeatureFlag`). Signed out, or in a staff session that holds no project, there
 * is no flag to wait for. The flag is the web's rollout: native hosts read nothing and wait for
 * nothing, so the desktop keeps its own sidebar preference and iOS its own shells.
 */
export function DesignV2Gate({ fallback, children }: DesignV2GateProps) {
    const { native } = useAppearancePolicy();
    return native ? (
        <>{children}</>
    ) : (
        <WebDesignV2Gate fallback={fallback}>{children}</WebDesignV2Gate>
    );
}

function WebDesignV2Gate({ fallback, children }: DesignV2GateProps) {
    const noProject = useTenantOrganizationId() === undefined;
    // `useFeatureFlag` directly, not `useFlag`: the gate is on the first-load path, and `useFlag`
    // would pull the whole generated flag registry into that bundle. False = the registry default.
    const { enabled, isAuthoritative, readFailed } = useFeatureFlag(
        "features.design_v2.enabled",
        false,
    );
    // A failed attempt ends the first read at once: retries (seconds of backoff) never hold the
    // pages, and the value they bring waits for the next load like any later one.
    const read = noProject || isAuthoritative === true || readFailed === true;
    useEffect(() => {
        if (read) decideTenantDesignV2(enabled);
    }, [read, enabled]);
    useEffect(() => () => leaveTenantDesignV2(), []);
    // The store, not `read`: the pages' first render must already see the decided design through
    // `RedesignProvider`, which re-renders from the same store.
    return useDesignV2Decided() ? <>{children}</> : <>{fallback}</>;
}
